import { useEffect, useState } from "react";
import { Clock, Lock } from "lucide-react";
import api from "@/lib/api";

const fmtLock = (iso) =>
  new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) + " UK";

/** Live countdown to the next prediction lock — one league, or the soonest across the user's leagues. */
export default function DeadlineBanner({ compact = false, competitionId = null }) {
  const [info, setInfo] = useState(null);
  const [now, setNow] = useState(Date.now());
  const lockAt = info?.next_lock_at;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/deadlines/next", { params: competitionId ? { competition_id: competitionId } : {} });
        if (!cancelled) setInfo(data);
      } catch {
        /* ignore */
      }
    };
    load();
    // Refresh the lock reference every 5 minutes to catch DST changes / clock drift.
    const refresh = setInterval(load, 5 * 60 * 1000);
    // Tick every second so the countdown updates smoothly.
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { cancelled = true; clearInterval(refresh); clearInterval(tick); };
  }, [competitionId]);

  if (!lockAt) return null;
  const targetMs = new Date(lockAt).getTime();
  const remaining = Math.max(0, Math.floor((targetMs - now) / 1000));

  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const mins = Math.floor((remaining % 3600) / 60);
  const secs = remaining % 60;

  // Under 6h → red/orange urgency
  const urgent = remaining <= 6 * 3600;
  const veryUrgent = remaining <= 60 * 60;

  const parts = [
    { v: days, l: "d" },
    { v: hours, l: "h" },
    { v: mins, l: "m" },
    { v: secs, l: "s" },
  ];

  if (compact) {
    return (
      <div
        data-testid="deadline-banner-compact"
        className={`hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${
          veryUrgent ? "border-red-500/50 bg-red-500/10 text-red-300"
            : urgent ? "border-[#F5B942]/40 bg-[#F5B942]/10 text-[#F5B942]"
                     : "border-[#1F3A5C] bg-[#0D2038] text-[#AAB7C7]"
        }`}
        title={`${info.competition_name} · locks ${fmtLock(lockAt)}`}
      >
        <Clock className="w-3.5 h-3.5" />
        <span className="tracking-wider text-[10px] hidden lg:inline max-w-[160px] truncate" data-testid="deadline-league-name">
          <span className="normal-case">{info.competition_name}</span> <span className="uppercase">· lock in</span>
        </span>
        <span className="font-mono font-semibold" data-testid="deadline-countdown">
          {days > 0 && `${days}d `}{String(hours).padStart(2, "0")}:{String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="deadline-banner"
      className={`p-4 sm:p-5 rounded-2xl border ${
        veryUrgent ? "border-red-500/50 bg-red-500/5"
          : urgent ? "border-[#F5B942]/40 bg-[#F5B942]/5"
                   : "border-[#1F3A5C] bg-[#0D2038]"
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            veryUrgent ? "bg-red-500/15 text-red-400"
              : urgent ? "bg-[#F5B942]/15 text-[#F5B942]"
                       : "bg-[#C7F500]/15 text-[#C7F500]"
          }`}>
            {remaining === 0 ? <Lock className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#AAB7C7]">
              Next prediction lock{!competitionId && ` · ${info.competition_name}`}{info.week_number ? ` · GW${info.week_number}` : ""}
            </div>
            <div className="font-heading text-base font-semibold" data-testid="deadline-lock-label">
              {remaining === 0 ? "Predictions locked" : fmtLock(lockAt)}
            </div>
          </div>
        </div>
        <div className="flex gap-2" data-testid="deadline-timer">
          {remaining === 0 ? (
            <div className="text-sm text-red-400 font-semibold">Only admins can edit now.</div>
          ) : (
            parts.map((p, i) => (
              <div key={i} className="min-w-[52px] text-center px-2 py-1.5 rounded-lg bg-[#071426] border border-[#1F3A5C]">
                <div className="font-heading text-xl font-bold leading-none">{String(p.v).padStart(2, "0")}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#AAB7C7] mt-0.5">{p.l}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
